import React, { useCallback, useEffect, useState } from 'react';
import ErrorMessage from '../../../../Elements/ErrorMessage';
import FactChips from '../../../../Elements/FactChips';
import FigmaBadge from '../../../../Elements/FigmaBadge';
import FigmaButton from '../../../../Elements/FigmaButton';
import RedirectLinkComponent from '../../../../Elements/RedirectLinkComponent';
import UserNameComponent from '../../../../Elements/UserNameComponent';
import GeneralEditWizzardBase from '../../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import Loading from '../../../../Elements/loading';
import ButtonColorsEnum from '../../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../../Enums/ButtonTypeEnum';
import CrudTypesEnum from '../../../../Enums/CrudTypesEnum';
import InvoiceAutomationItem from '../../../../Domain/ThirdParty/Project/InvoiceAutomationItem';
import InvoiceAutomationService from '../../../../Services/InvoiceAutomationService';
import contactService from '../../../../Services/contactService';
import RoleService from '../../../../Services/roleService';
import dateTimeHelper from '../../../../Helpers/datetimeHelper';
import useBackendData from '../../../../Helpers/useBackendData';
import {
  getActivationQuestion,
  getInvoicedWithStatusText,
  getSetupStatusText,
} from '../../../../Helpers/invoiceAutomationSetupPageHelper';
import { formatProjectLabel } from '../../../../Helpers/invoiceAutomationEditorHelper';
import InvoiceAutomationStatusChip from './InvoiceAutomationStatusChip';
import InvoiceAutomationWhenStep from './InvoiceAutomationWhenStep';
import InvoiceAutomationInvoiceStep from './InvoiceAutomationInvoiceStep';
import InvoiceAutomationWindowDescription from './InvoiceAutomationWindowDescription';
import InvoiceAutomationProjectsWindowDescription from './InvoiceAutomationProjectsWindowDescription';
import InvoiceAutomationHistory from './InvoiceAutomationHistory';
import InvoiceAutomationRunNow from './InvoiceAutomationRunNow';
import InvoiceAutomationSuggestion from './InvoiceAutomationSuggestion';

// A project's automatic invoicing, on the project popup's Automatic Invoicing tab: the status with Activate or
// Deactivate and Run Now, the facts it works with, its two steps (when, and the invoice, edited in the edit wizard)
// and the history of its runs. Without a setup it says so, where a new one would start from, and offers Set up.
// onSetupChange gets the saved setup (an InvoiceAutomationItem) each time it is saved or switched on or off, so that
// the rest of the project popup shows the new status and the projects list loads again when the popup closes.
export default function InvoiceAutomationSetupPage({ project = null, onSetupChange = (savedSetup) => {} }) {
  const [setup, setSetup] = useState(null);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isProjectsEditorOpen, setIsProjectsEditorOpen] = useState(false);
  const [isRunNowOpen, setIsRunNowOpen] = useState(false);
  const canEdit = RoleService.CheckUserPermission('ButtonRowButton', 'invoice_automation_edit');

  const loadSetup = useCallback(
    () => InvoiceAutomationService.GetInvoiceAutomation(project.ProjectId),
    [project.ProjectId]
  );
  const { data: loadedSetup, error: loadError } = useBackendData(
    loadSetup,
    'Error! ErrorCode: IASP46. Failed to load the automatic invoicing setup.'
  );
  useEffect(() => {
    if (loadedSetup) {
      setSetup(new InvoiceAutomationItem(loadedSetup, project));
    }
  }, [loadedSetup, project]);

  // With their addresses, as the invoice form offers them for the bill-to contact.
  const loadContacts = useCallback(
    () => contactService.GetContactListByClientId(project.BravoClientId, true),
    [project.BravoClientId]
  );
  const { data: contacts } = useBackendData(
    loadContacts,
    "Error! ErrorCode: IASP61. Failed to load the client's contacts."
  );

  if (loadError) {
    return <p className='iauto-page'>{loadError}</p>;
  }
  if (setup === null) {
    return (
      <div className='iauto-page'>
        <Loading />
      </div>
    );
  }

  const isActive = setup.IsActive === true;
  const contact = (contacts ?? []).find((candidate) => candidate.contactId === Number(setup.ContactId));
  const contactName = contact ? contact.name : null;

  // Saves a choice changed in step 1; resolves with the backend's refusal, or with null once the saved setup is shown.
  function saveChanges(changes) {
    return InvoiceAutomationService.UpsertInvoiceAutomation({ ...setup, ...changes })
      .then((savedSetup) => {
        if (savedSetup?.error) {
          return savedSetup.error;
        }
        const savedItem = new InvoiceAutomationItem(savedSetup, project);
        setSetup(savedItem);
        onSetupChange(savedItem);
        ErrorMessage.SetNotification('Automatic invoicing saved.');
        return null;
      })
      .catch(() => 'Error! ErrorCode: IASP89. Failed to save the automatic invoicing setup.');
  }

  // The invoice, or the projects on it, saved in their edit window, once its success message is closed: the saved
  // setup is shown.
  function showSavedSetup(editedSetup, savedSetup) {
    setIsEditorOpen(false);
    setIsProjectsEditorOpen(false);
    if (savedSetup) {
      const savedItem = new InvoiceAutomationItem(savedSetup, project);
      setSetup(savedItem);
      onSetupChange(savedItem);
    }
  }

  // A choice picked in a sentence of step 1 shows at once and is saved; it goes back when the save fails.
  function saveChoice(changes) {
    const setupBefore = setup;
    setSetup({ ...setup, ...changes });
    setIsSaving(true);
    saveChanges(changes)
      .then((refusal) => {
        if (refusal) {
          setSetup(setupBefore);
          ErrorMessage.SetErrorMessage(refusal);
        }
      })
      .finally(() => setIsSaving(false));
  }

  function setActive(isToBeActive) {
    setIsSaving(true);
    InvoiceAutomationService.SetInvoiceAutomationActive(project.ProjectId, isToBeActive)
      .then((savedSetup) => {
        if (savedSetup?.error) {
          ErrorMessage.SetErrorMessage(savedSetup.error);
          return;
        }
        const savedItem = new InvoiceAutomationItem(savedSetup, project);
        setSetup(savedItem);
        onSetupChange(savedItem);
        ErrorMessage.SetNotification(
          isToBeActive ? 'Automatic invoicing activated.' : 'Automatic invoicing deactivated.'
        );
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('Error! ErrorCode: IASP123. Failed to change the status of automatic invoicing.');
      })
      .finally(() => setIsSaving(false));
  }

  // A run may have made a draft, which becomes the project's latest invoice, and it adds a run to the history.
  function closeRunNow() {
    setIsRunNowOpen(false);
    setHistoryVersion((version) => version + 1);
    InvoiceAutomationService.GetInvoiceAutomation(project.ProjectId)
      .then((reloadedSetup) => {
        if (!reloadedSetup?.error) {
          setSetup(new InvoiceAutomationItem(reloadedSetup, project));
        }
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('Error! ErrorCode: IASP139. Failed to load the automatic invoicing setup.');
      });
  }

  // The line beside the status chip; a project on another setup's invoice names that setup's project instead.
  function getStatusText() {
    if (setup.InvoicedWithProjectId) {
      return getInvoicedWithStatusText(setup.InvoicedWithProjectId, setup.InvoicedWithProjectName);
    }
    return getSetupStatusText(setup.IsNewSetup, isActive, setup.RunSchedule);
  }

  function renderStatus() {
    // A project on another setup's invoice has no setup, so no status, of its own.
    if (setup.InvoicedWithProjectId) {
      return null;
    }
    // Grey, as nothing is on or off yet: green and red mean Active and Deactivated.
    if (setup.IsNewSetup) {
      return (
        <FigmaBadge
          variant='todo'
          label='Not set up'
        />
      );
    }
    return (
      <InvoiceAutomationStatusChip
        isActive={isActive}
        runSchedule={setup.RunSchedule}
      />
    );
  }

  function renderActions() {
    if (setup.IsNewSetup) {
      return null;
    }
    return (
      <div className='iauto-actions'>
        {isActive && (
          <FigmaButton
            label='Run Now'
            type={ButtonTypeEnum.NormalNormal}
            color={ButtonColorsEnum.Primary}
            roleKey='invoice_automation_edit'
            disabled={isSaving}
            onClick={() => setIsRunNowOpen(true)}
          />
        )}
        <FigmaButton
          label={isActive ? 'Deactivate' : 'Activate'}
          type={ButtonTypeEnum.NormalNormal}
          color={isActive ? ButtonColorsEnum.Common : ButtonColorsEnum.Primary}
          roleKey='invoice_automation_edit'
          disabled={isSaving}
          onClick={() =>
            ErrorMessage.SetApprove(getActivationQuestion(!isActive, setup.RunSchedule), () => setActive(!isActive))
          }
        />
      </div>
    );
  }

  function getFacts() {
    const projectDates = `${dateTimeHelper.toYYYYsMMsDD(project.StartDate)} – ${dateTimeHelper.toYYYYsMMsDD(
      project.EndDate
    )}`;
    const latestInvoice = setup.LatestTpClientInvoiceId ? (
      <RedirectLinkComponent
        isFigmaStyle={true}
        path='tpclientinvoicedetails'
        tooltipText={setup.LatestTpClientInvoiceId}
        text={setup.LatestTpClientInvoiceId}
        keyValuePairs={[{ key: 'id', value: setup.LatestTpClientInvoiceId }]}
      />
    ) : null;
    const lastChanged = setup.LastChangedByUserId ? (
      <>
        <UserNameComponent userId={setup.LastChangedByUserId} />,{' '}
        {dateTimeHelper.utcToLocalYYYYsMMsDDsHHsMM(setup.LastChangedOnUtc)}
      </>
    ) : null;
    return [
      { label: 'Client', value: project.ClientName },
      { label: 'Office', value: project.OfficeName },
      { label: 'Project dates', value: projectDates },
      { label: 'Latest invoice', value: latestInvoice },
      { label: 'Last changed', value: lastChanged },
    ];
  }

  // The design-system empty state, in a dashed card: neutral, as nothing is on or off yet. A project with an invoice
  // gets the setup Set Up starts from as a suggestion that stands out; without one, the invoice form's defaults. A
  // project on another setup's invoice gets no Set Up: that setup invoices its jobs.
  function renderNotSetUp() {
    if (setup.InvoicedWithProjectId) {
      return renderInvoicedWithAnotherProject();
    }
    const hasLatestInvoice = !!setup.LatestTpClientInvoiceId;
    return (
      <div className='iauto-empty'>
        <div className='empty-state'>
          <div className='ei'>
            <i className='fa-solid fa-file-invoice' />
          </div>
          <b>Automatic invoicing is not set up</b>
          <span>
            Once it is set up and activated, Bravo makes this project&apos;s draft invoices and adds its ready jobs to
            them.{hasLatestInvoice ? '' : ' A new setup starts from the same defaults as the invoice form.'}
          </span>
          {hasLatestInvoice && <InvoiceAutomationSuggestion invoiceId={setup.LatestTpClientInvoiceId} />}
          <FigmaButton
            label='Set Up'
            type={ButtonTypeEnum.NormalNormal}
            color={ButtonColorsEnum.Primary}
            roleKey='invoice_automation_edit'
            onClick={() => setIsEditorOpen(true)}
          />
        </div>
      </div>
    );
  }

  function renderInvoicedWithAnotherProject() {
    const otherProject = formatProjectLabel(setup.InvoicedWithProjectId, setup.InvoicedWithProjectName);
    return (
      <div className='iauto-empty'>
        <div className='empty-state'>
          <div className='ei'>
            <i className='fa-solid fa-link' />
          </div>
          <b>This project is on the invoice of project {otherProject}</b>
          <span>
            Its ready jobs go on the drafts that project&apos;s automatic invoicing makes. Its setup, its runs and its
            history are on that project.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className='iauto-page'>
      <div className='iauto-head'>
        <div className='iauto-status'>
          {renderStatus()}
          <span className='iauto-status-text'>{getStatusText()}</span>
        </div>
        {renderActions()}
      </div>
      <FactChips facts={getFacts()} />
      {setup.IsNewSetup ? (
        renderNotSetUp()
      ) : (
        <>
          <div className='iauto-steps'>
            <InvoiceAutomationWhenStep
              setup={setup}
              isDisabled={!canEdit || isSaving}
              onChange={saveChoice}
            />
            <InvoiceAutomationInvoiceStep
              setup={setup}
              clientName={project.ClientName}
              contactName={contactName}
              canEdit={canEdit}
              onEdit={() => setIsEditorOpen(true)}
              onChangeProjects={() => setIsProjectsEditorOpen(true)}
            />
          </div>
          <h2 className='iauto-section-title'>History</h2>
          <InvoiceAutomationHistory
            key={historyVersion}
            projectId={project.ProjectId}
            projectName={project.ProjectName}
            clientId={project.BravoClientId}
          />
        </>
      )}
      {isEditorOpen && (
        <GeneralEditWizzardBase
          crudType={setup.IsNewSetup ? CrudTypesEnum.Create : CrudTypesEnum.Update}
          editedItem={{ ...setup, ClientContacts: contacts ?? [] }}
          clickOnCancel={() => setIsEditorOpen(false)}
          clickOnSave={showSavedSetup}
          windowDescription={InvoiceAutomationWindowDescription}
          errorCode='IASP304'
        />
      )}
      {isProjectsEditorOpen && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Update}
          editedItem={{ ...setup }}
          clickOnCancel={() => setIsProjectsEditorOpen(false)}
          clickOnSave={showSavedSetup}
          windowDescription={InvoiceAutomationProjectsWindowDescription}
          errorCode='IASP337'
        />
      )}
      {isRunNowOpen && (
        <InvoiceAutomationRunNow
          projectId={project.ProjectId}
          projectName={project.ProjectName}
          onClose={closeRunNow}
        />
      )}
    </div>
  );
}
